import type {
  EquipmentRecord,
  EquippableItem,
  EquipmentSlot,
  Item,
  ItemStack,
} from "../types/items.js";
import { emptyEquipment } from "../party/equipment.js";

/**
 * Per-character inventory — flat slot list plus fixed equipment record.
 *
 * Diablo-style grid packing is out of scope (`specs/inventory-and-economy.md`):
 * items live in a simple ordered list, gated only by weight. Weight capacity
 * is driven by STR on a linear curve:
 *
 *     capacity = CARRY_BASE + STR × CARRY_PER_STR    (spec: 50 + STR*5)
 *
 * Stackable items (arrows, rations, potions, gold) merge into a single entry;
 * non-stackables always occupy a fresh one-item stack. Zero-weight quest /
 * key items (`weightLbs === 0`) contribute nothing to the total — they're
 * still tracked but never cause a capacity refusal.
 *
 * All mutators return a new `CharacterInventory` so the shape is friendly to
 * store snapshots and time-travel debugging. No in-place mutation.
 */

// ── Tunable constants ────────────────────────────────────────────────────────

/** Base pounds a character can carry with STR 0. */
export const CARRY_BASE_LBS = 50;
/** Extra pounds of capacity per point of STR. STR 10 → 100 lb, STR 18 → 140 lb. */
export const CARRY_PER_STR_LBS = 5;

// ── Types ────────────────────────────────────────────────────────────────────

/**
 * A slot entry in the carry list. Stackables roll up into one entry.
 *
 * Re-export of the canonical {@link ItemStack} — kept under the old name
 * so existing imports (and intent: "a stack *inside* an inventory") keep
 * reading cleanly.
 */
export type InventoryStack = ItemStack;

export interface CharacterInventory {
  readonly stacks: readonly ItemStack[];
  readonly equipment: EquipmentRecord;
}

// ── Factory ──────────────────────────────────────────────────────────────────

export function emptyInventory(): CharacterInventory {
  return { stacks: [], equipment: emptyEquipment() };
}

// ── Capacity math ────────────────────────────────────────────────────────────

/** Lbs a character can carry given their Strength score. */
export function carryCapacity(strengthScore: number): number {
  return CARRY_BASE_LBS + CARRY_PER_STR_LBS * strengthScore;
}

/**
 * Total pounds currently carried — sum of every stack's `item.weightLbs * quantity`
 * plus the weight of every equipped item. Gold is weightless per spec; items
 * with `weightLbs === 0` (quest items, keys) contribute nothing.
 */
export function totalInventoryWeight(inv: CharacterInventory): number {
  let carried = 0;
  for (const stack of inv.stacks) {
    carried += stack.item.weightLbs * stack.quantity;
  }
  for (const slot of Object.values(inv.equipment)) {
    if (slot !== null) carried += slot.weightLbs;
  }
  return carried;
}

/** Capacity minus current load. Can be zero but never negative (clamped). */
export function remainingCapacity(inv: CharacterInventory, strengthScore: number): number {
  return Math.max(0, carryCapacity(strengthScore) - totalInventoryWeight(inv));
}

// ── Add / remove ─────────────────────────────────────────────────────────────

export interface AddResult {
  readonly inventory: CharacterInventory;
  /** Quantity that actually fit into the inventory. */
  readonly added: number;
  /** Quantity that didn't fit (weight-gated). Caller decides what to do with the remainder. */
  readonly refused: number;
}

/**
 * Add `quantity` of `item` to the inventory, respecting the character's
 * carry capacity.
 *
 * Stackable items merge into the first matching stack. Non-stackables create
 * one fresh entry per unit and refuse the rest when the weight budget runs
 * out. Zero-weight items always succeed entirely (no capacity check).
 */
export function addItem(
  inv: CharacterInventory,
  item: Item,
  quantity: number,
  strengthScore: number,
): AddResult {
  if (quantity <= 0) return { inventory: inv, added: 0, refused: 0 };

  // Zero-weight fast-path: everything fits without capacity math.
  if (item.weightLbs === 0) {
    return {
      inventory: mergeOrAppend(inv, item, quantity),
      added: quantity,
      refused: 0,
    };
  }

  const remaining = remainingCapacity(inv, strengthScore);
  const canFit = Math.min(quantity, Math.floor(remaining / item.weightLbs));
  if (canFit <= 0) {
    return { inventory: inv, added: 0, refused: quantity };
  }

  return {
    inventory: mergeOrAppend(inv, item, canFit),
    added: canFit,
    refused: quantity - canFit,
  };
}

export interface RemoveResult {
  readonly inventory: CharacterInventory;
  /** Quantity actually removed; may be less than requested if the stack was short. */
  readonly removed: number;
}

/**
 * Remove up to `quantity` of `itemId` from the inventory. Drains the first
 * matching stack; if a stack empties it is pruned from the list.
 */
export function removeItem(
  inv: CharacterInventory,
  itemId: string,
  quantity: number,
): RemoveResult {
  if (quantity <= 0) return { inventory: inv, removed: 0 };

  const idx = inv.stacks.findIndex((s) => s.item.id === itemId);
  if (idx < 0) return { inventory: inv, removed: 0 };

  const stack = inv.stacks[idx]!;
  const take = Math.min(quantity, stack.quantity);
  const nextStacks = [...inv.stacks];
  if (take === stack.quantity) {
    nextStacks.splice(idx, 1);
  } else {
    nextStacks[idx] = { item: stack.item, quantity: stack.quantity - take };
  }
  return {
    inventory: { ...inv, stacks: nextStacks },
    removed: take,
  };
}

// ── Equipment wiring ─────────────────────────────────────────────────────────

/** Replace a single slot on the equipment record. Leaves stacks untouched. */
export function setEquipment(
  inv: CharacterInventory,
  slot: EquipmentSlot,
  item: EquippableItem | null,
): CharacterInventory {
  return { ...inv, equipment: { ...inv.equipment, [slot]: item } };
}

// ── Inter-character transfer (I11) ──────────────────────────────────────────

export interface TransferResult {
  readonly source: CharacterInventory;
  readonly destination: CharacterInventory;
  /** Quantity that successfully moved. 0 = source didn't have any. */
  readonly transferred: number;
  /** Quantity removed from source but refused by the destination's capacity. */
  readonly refused: number;
}

/**
 * Move up to `quantity` of `itemId` from `source` to `destination`.
 *
 * Removes from the source first, then tries to add to the destination.
 * Any quantity the destination refuses (weight capped) is pushed back
 * onto the source so the transfer is atomic from the player's
 * perspective: the item is never destroyed by a half-failed move.
 *
 * Works for both in- and out-of-combat — callers decide whether to
 * gate on combat state, which keeps this module free of turn-order
 * concerns.
 */
export function transferItem(
  source: CharacterInventory,
  destination: CharacterInventory,
  itemId: string,
  quantity: number,
  destinationStrengthScore: number,
  sourceStrengthScore?: number,
): TransferResult {
  if (quantity <= 0) {
    return { source, destination, transferred: 0, refused: 0 };
  }

  const stack = source.stacks.find((s) => s.item.id === itemId);
  if (stack === undefined) {
    return { source, destination, transferred: 0, refused: 0 };
  }

  const removed = removeItem(source, itemId, quantity);
  const added = addItem(destination, stack.item, removed.removed, destinationStrengthScore);

  if (added.refused === 0) {
    return {
      source: removed.inventory,
      destination: added.inventory,
      transferred: added.added,
      refused: 0,
    };
  }

  // Put the refused quantity back on the source — transfer is atomic.
  // Items that were already carried under the source's current STR must
  // still fit under that same STR after the round-trip, so use the
  // source's actual capacity when the caller provides it. Default to
  // `Infinity` when the caller doesn't know (e.g. legacy tests) — the
  // refused stack was in-source a moment ago, so over-capacity cannot
  // happen unless STR changed between calls.
  const restoreStr = sourceStrengthScore ?? Number.POSITIVE_INFINITY;
  const restored = addItem(removed.inventory, stack.item, added.refused, restoreStr);
  return {
    source: restored.inventory,
    destination: added.inventory,
    transferred: added.added,
    refused: added.refused,
  };
}

// ── Internals ────────────────────────────────────────────────────────────────

function mergeOrAppend(
  inv: CharacterInventory,
  item: Item,
  quantity: number,
): CharacterInventory {
  const stacks = [...inv.stacks];
  if (item.stackable) {
    const idx = stacks.findIndex((s) => s.item.id === item.id);
    if (idx >= 0) {
      const existing = stacks[idx]!;
      stacks[idx] = { item: existing.item, quantity: existing.quantity + quantity };
      return { ...inv, stacks };
    }
    stacks.push({ item, quantity });
    return { ...inv, stacks };
  }
  // Non-stackable: one entry per copy.
  for (let i = 0; i < quantity; i++) stacks.push({ item, quantity: 1 });
  return { ...inv, stacks };
}
